import API_CONFIG from '../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';

// Types for profit data
export interface DailyProfitSummary {
  trade_date: string;
  product_id?: string;
  gross_profit: number;
  net_profit: number;
  trade_count: number;
  fees_paid: number;
}

export interface HourlyProfitBreakdown {
  trade_date: string;
  trade_hour: number;
  product_id?: string;
  gross_profit: number;
  net_profit: number;
  trade_count: number;
}

export interface AssetBreakdownSummary {
  product_id: string;
  net_profit: number;
  gross_profit: number;
  trade_count: number;
  fees_paid: number;
  percent_of_total: number;
  avg_profit_per_trade: number;
}

export interface DailyProfitPivot {
  last_fill_date: string;
  ADA_USDC: number;
  AVAX_USDC: number;
  BTC_USDC: number;
  ETH_USDC: number;
  LINK_USDC: number;
  SOL_USDC: number;
  SUI_USDC: number;
  XRP_USDC: number;
  total_profit: number;
}

export interface ProfitMobileSummary {
  today: DailyProfitSummary;
  yesterday: DailyProfitSummary;
  change_amount: number;
  change_percentage: number;
  asset_breakdown: DailyProfitSummary[];
  seven_day_trend: DailyProfitSummary[];
  last_updated: string;
}

/**
 * Get the current user's authentication token
 */
async function getAuthToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return undefined;
  }
}

/**
 * Generic API fetch wrapper with timeout and error handling
 */
async function apiFetch<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_CONFIG.profitBaseUrl}${endpoint}`;

  // Check if we're using a placeholder URL (local development)
  if (API_CONFIG.profitBaseUrl.includes('your-api-gateway-url') || API_CONFIG.profitBaseUrl === 'http://localhost:3000') {
    console.error('Profit API not configured. Please set REACT_APP_PROFIT_API_BASE_URL environment variable.');
    throw new Error('API endpoint not configured. Please configure the Profit API endpoint.');
  }

  // Get authentication token
  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please log in.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        ...options?.headers,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} - ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      throw error;
    }

    throw new Error('Unknown error occurred');
  }
}

// Profit API functions

/**
 * Get mobile-optimized profit summary
 */
export async function getProfitSummary(date?: string, productId?: string): Promise<ProfitMobileSummary> {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  if (productId) params.append('product', productId);

  const query = params.toString() ? `?${params.toString()}` : '';
  return apiFetch<ProfitMobileSummary>(`/v1/profit/summary${query}`);
}

/**
 * Get daily profit for a specific date
 */
export async function getDailyProfit(
  date: string,
  productId?: string
): Promise<DailyProfitSummary | DailyProfitSummary[]> {
  const query = productId ? `?product=${encodeURIComponent(productId)}` : '';
  return apiFetch<DailyProfitSummary | DailyProfitSummary[]>(`/v1/profit/daily/${date}${query}`);
}

/**
 * Get daily profit range
 */
export async function getDailyProfitRange(
  startDate: string,
  endDate: string,
  productId?: string
): Promise<DailyProfitSummary[]> {
  const query = new URLSearchParams({
    start: startDate,
    end: endDate,
    ...(productId && { product: productId })
  });
  return apiFetch<DailyProfitSummary[]>(`/v1/profit/daily/range?${query}`);
}

/**
 * Get hourly profit breakdown
 */
export async function getHourlyProfit(
  date: string,
  productId?: string
): Promise<HourlyProfitBreakdown[]> {
  const query = productId ? `?product=${encodeURIComponent(productId)}` : '';
  return apiFetch<HourlyProfitBreakdown[]>(`/v1/profit/hourly/${date}${query}`);
}

/**
 * Get asset breakdown for a date range
 * Returns profit aggregated by product_id with percentages and averages calculated
 */
export async function getAssetBreakdown(
  startDate: string,
  endDate: string
): Promise<AssetBreakdownSummary[]> {
  const query = new URLSearchParams({
    start: startDate,
    end: endDate,
  });
  return apiFetch<AssetBreakdownSummary[]>(`/v1/profit/breakdown/assets?${query}`);
}

/**
 * Get daily profit pivot data for a date range
 * Returns daily profit with each product as a separate column
 */
export async function getDailyProfitPivot(
  startDate: string,
  endDate: string
): Promise<DailyProfitPivot[]> {
  const query = new URLSearchParams({
    start: startDate,
    end: endDate,
  });
  return apiFetch<DailyProfitPivot[]>(`/v1/profit/pivot/daily?${query}`);
}
